import { AuthenticatedUserLayout } from "@/components/AuthenticatedUserLayout";
import { CreditsExhaustionModal } from "@/components/billing/CreditsExhaustionModal";
import { PaymentFailureBanner } from "@/components/billing/PaymentFailureBanner";
import { VerifyCardModal } from "@/components/billing/VerifyCardModal";
import { ExceptionErrorBoundary } from "@/components/ExceptionHandler";
import { Flags } from "@/feature-flags";
import { PageSuspense } from "@/components/PageSuspense";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { RunnerDetailsPage } from "@/components/runners/details/RunnerDetailsPage";
import { SettingsPageLayout } from "@/components/settings/SettingsPageLayout";
import { SettingsSidebar } from "@/components/settings/SettingsSidebar";
import { MainContent, NoSidebar, SidebarLayout } from "@/components/SidebarLayout";
import { TierViolationModal } from "@/components/tiering/TierViolationDialog";
import { useEnsureVscodeServer } from "@/hooks/use-ensure-vscode-server";
import { useEnvironmentSettingsCleanup } from "@/hooks/use-environment-settings-cleanup";
import { useFlagOnaAIEnabled } from "@/hooks/use-flag";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

function useHideAgentsPage() {
    const { value: isOnaAIEnabled, loading } = useFlagOnaAIEnabled();
    // Hide when Ona AI is NOT enabled
    return {
        value: !isOnaAIEnabled,
        loading,
    };
}

import { useGitpodCookie } from "@/hooks/use-gitpod-cookie";
import { useLoaded } from "@/hooks/use-loaded";
import { usePerformance } from "@/hooks/use-performance";
import { usePreventBrowserShortcuts } from "@/hooks/use-prevent-browser-shortcuts";
import { useButtonOrAnchorTracking } from "@/hooks/use-segment";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { type MenuItem } from "@/menu-item";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { AccountDeletedPage } from "@/routes/acconut-deleted/AccountDeletedPage";
import { OnaAIPage } from "@/routes/ai/OnaAIPage";
import { ConnectIntegrationPage } from "@/routes/integrations/ConnectIntegrationPage";
import { IntegrationConnectedPage } from "@/routes/integrations/IntegrationConnectedPage";
import { CreateOrganizationPage } from "@/routes/create-organization/CreateOrganizationPage";
import { createEnvironmentDetailsRoute } from "@/routes/environments/details/EnvironmentDetailsRoute";
import { EnvironmentCodeChanges } from "@/routes/environments/code-changes/EnvironmentCodeChanges";
import { InsightsPage } from "@/routes/insights/InsightsPage";
import { JOIN_ORGANIZATION_PATH } from "@/routes/join-organization/join-organization";
import { JoinOrganizationPage } from "@/routes/join-organization/JoinOrganizationPage";
import { LocalRunner, QuitApp } from "@/routes/local-runner/LocalRunner";
import { LoginPage } from "@/routes/login/LoginPage";
import { MagiclinkPage } from "@/routes/magiclink/MagiclinkPage";
import { NotFound } from "@/routes/NotFound";
import { OnaOnboardingPage } from "@/routes/ona-onboarding/OnaOnboardingPage";
import { OnaOnboardingSetupPage } from "@/routes/ona-onboarding/OnaOnboardingSetupPage";
import { projectRoutes } from "@/routes/projects/project-routes";
import { LoginAndSecurityPage } from "@/routes/settings/login/LoginAndSecurityPage";
import { AgentsPage } from "@/routes/settings/organization/agents/AgentsPage";
import { OrgIntegrationsPage } from "@/routes/settings/organization/integrations/IntegrationsPage";
import BillingPage from "@/routes/settings/organization/billing/BillingPage";
import { EnvironmentInventoryPage } from "@/routes/settings/organization/environment-inventory/EnvironmentInventoryPage";
import { ManageOrganizationPage } from "@/routes/settings/organization/manage-organization/ManageOrganizationPage";
import { InviteMembersPage } from "@/routes/settings/organization/members/InviteMembersPage";
import { MembersPage } from "@/routes/settings/organization/members/MembersPage";
import { GroupDetailsPage } from "@/routes/settings/organization/members/GroupDetailsPage";
import CreditUsagePage from "@/routes/settings/organization/credit-usage/CreditUsagePage";
import { ExperimentsSettingsPage } from "@/routes/settings/user/experiments/ExperimentsSettingsPage";
import { ExperimentsActivationPage } from "@/routes/experiments/ExperimentsActivationPage";
import { AnnouncementBannerPage } from "@/routes/settings/organization/announcements/AnnouncementBannerPage";
import { OrganizationPoliciesPage } from "@/routes/settings/organization/policies/OrganizationPoliciesPage";
import { RunnersPage } from "@/routes/settings/organization/runners/RunnersPage";
import { OrganizationSecretsPage } from "@/routes/settings/organization/secrets/OrganizationSecretsPage";
import { GitAuthenticationsPage } from "@/routes/settings/user/git-authentications/GitAuthenticationsPage";
import { PersonalAccessTokensPage } from "@/routes/settings/user/personal-access-tokens/PersonalAccessTokensPage";
import { UserPreferencesPage } from "@/routes/settings/user/preferences/UserPreferencesPage";
import { UserProfilePage } from "@/routes/settings/user/profile/UserProfilePage";
import { UserSecretsPage } from "@/routes/settings/user/secrets/UserSecretsPage";
import { IntegrationsPage as UserIntegrationsPage } from "@/routes/settings/user/integrations/IntegrationsPage";
import { SigninDesktopPage } from "@/routes/signin-desktop/SigninDesktopPage";
import { SettingsRedirect } from "@/routes/settings/SettingsRedirect";
import { UserSettingsRedirect } from "@/routes/settings/UserSettingsRedirect";
import { SlashRedirect } from "@/routes/SlashRedirect";
import { AutomationDetailsPage } from "@/routes/automations/automation-details-page/AutomationDetailsPage";
import { AutomationEditPage } from "@/routes/automations/automation-edit-page/AutomationEditPage";
import { AutomationExecutionLayout } from "@/routes/automations/AutomationExecutionLayout";
import { AutomationExecutionSummaryPage } from "@/routes/automations/AutomationExecutionSummaryPage";
import { AutomationExecutionActionPage } from "@/routes/automations/AutomationExecutionActionPage";
import { AutomationsPage } from "@/routes/automations/AutomationsPage";
import { NewAutomationPage } from "@/routes/automations/NewAutomationPage";
import { RequireFullAutomationAccess } from "@/routes/automations/RequireFullAutomationAccess";
import { RequireWebhooksCapability } from "@/routes/automations/RequireWebhooksCapability";
import { WebhookDetailsPage } from "@/routes/automations/WebhookDetailsPage";
import { WebhooksSidePanel } from "@/components/webhooks/WebhooksSidePanel";
import { useEffect, useState, type FC } from "react";
import { createMemoryRouter, Navigate, Outlet, RouterProvider, type RouteObject } from "react-router-dom";
import { sentryCreateBrowserRouter } from "@/sentry";
import { ServiceAccountDetailsPage } from "@/routes/settings/organization/members/ServiceAccountDetailsPage";
import { ScmSuccessPage } from "@/routes/runner/ScmSuccessPage";
import type { RouteHandle } from "@/route-handle";

export const App: FC = () => {
    useLoaded();
    useButtonOrAnchorTracking();
    useGitpodCookie();
    useEnsureVscodeServer();
    useEnvironmentSettingsCleanup();
    usePreventBrowserShortcuts(true);

    // Initialize app-level performance tracking
    const { markAppReady } = usePerformance({
        component: "App",
    });

    // Mark when React is ready and measure app load time
    useEffect(() => {
        markAppReady();
    }, [markAppReady]);

    // We don't wrap this in an error boundary as the RouterProvider provides its own error boundary which would be triggered
    // before ours. Instead we have a single rootRoute RouteObject which provides the error boundary as the errorElement.
    return <RouterProvider router={NewSidebarRouter} />;
};

// eslint-disable-next-line react-refresh/only-export-components
export const settingsRoutes: RouteObject = {
    element: <SettingsPageLayout />,
    path: "/settings",
    children: [
        {
            index: true,
            handle: {},
            element: <SettingsRedirect />,
        },
        {
            path: "profile",
            element: (
                <UserSettingsRedirect>
                    <UserProfilePage />
                </UserSettingsRedirect>
            ),
            handle: {
                label: "Profile",
                menuItem: {
                    label: "Profile",
                    section: "My account",
                    to: "/settings/profile",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
        },
        {
            path: "preferences",
            element: (
                <UserSettingsRedirect>
                    <UserPreferencesPage />
                </UserSettingsRedirect>
            ),
            handle: {
                label: "Preferences",
                menuItem: {
                    label: "Preferences",
                    section: "My account",
                    to: "/settings/preferences",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
        },
        {
            path: "secrets",
            element: (
                <UserSettingsRedirect>
                    <UserSecretsPage />
                </UserSettingsRedirect>
            ),
            handle: {
                label: "Secrets",
                menuItem: {
                    label: "Secrets",
                    section: "My account",
                    to: "/settings/secrets",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
        },
        {
            path: "git-authentications",
            element: (
                <UserSettingsRedirect>
                    <GitAuthenticationsPage />
                </UserSettingsRedirect>
            ),
            handle: {
                label: "Git authentications",
                menuItem: {
                    label: "Git authentications",
                    section: "My account",
                    to: "/settings/git-authentications",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
        },
        {
            path: "personal-access-tokens",
            element: (
                <UserSettingsRedirect>
                    <PersonalAccessTokensPage />
                </UserSettingsRedirect>
            ),
            handle: {
                label: "Personal access tokens",
                menuItem: {
                    label: "Personal access tokens",
                    section: "My account",
                    to: "/settings/personal-access-tokens",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
        },
        {
            path: "user-integrations",
            element: (
                <UserSettingsRedirect>
                    <UserIntegrationsPage />
                </UserSettingsRedirect>
            ),
            handle: {
                label: "Integrations",
                menuItem: {
                    label: "Integrations",
                    section: "My account",
                    to: "/settings/user-integrations",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
        },
        {
            path: "experiments",
            element: <ExperimentsSettingsPage />,
            handle: {
                label: "Experiments",
                menuItem: {
                    label: "Experiments",
                    section: "My account",
                    to: "/settings/experiments",
                    isAdminRole: false,
                    hiddenOnTiers: [OrganizationTier.ENTERPRISE],
                } satisfies MenuItem,
            },
        },
        {
            path: "manage-organization",
            element: <ManageOrganizationPage />,
            handle: {
                label: "General",
                menuItem: {
                    label: "General",
                    section: "Organization",
                    to: "/settings/manage-organization",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "announcements",
            element: <AnnouncementBannerPage />,
            handle: {
                label: "Announcements",
                menuItem: {
                    label: "Announcements",
                    section: "Organization",
                    to: "/settings/announcements",
                    isAdminRole: true,
                    hiddenOnTiers: [OrganizationTier.FREE, OrganizationTier.FREE_ONA, OrganizationTier.CORE],
                } satisfies MenuItem,
            },
        },
        {
            path: "members",
            handle: {
                label: "Members",
                menuItem: {
                    label: "Members",
                    section: "Organization",
                    to: "/settings/members",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
            children: [
                {
                    index: true,
                    element: <MembersPage />,
                },
                {
                    path: "invite",
                    element: <InviteMembersPage />,
                    handle: {
                        label: "Invite members",
                    },
                },
                {
                    path: "group/:groupId",
                    element: <GroupDetailsPage />,
                    handle: {
                        label: "Group Details",
                    },
                },
                {
                    path: "service-account/:serviceAccountId",
                    element: <ServiceAccountDetailsPage />,
                    handle: {
                        label: "Service Account Details",
                    },
                },
            ],
        },
        {
            path: "runners",
            handle: {
                label: "Runners",
                menuItem: {
                    label: "Runners",
                    section: "Organization",
                    to: "/settings/runners",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
            children: [
                {
                    index: true,
                    element: <RunnersPage />,
                },
                {
                    path: ":runnerId",
                    element: <RunnerDetailsPage />,
                },
            ],
        },
        {
            path: "environments",
            element: <EnvironmentInventoryPage />,
            handle: {
                label: "Environments",
                menuItem: {
                    label: "Environments",
                    section: "Organization",
                    to: "/settings/environments",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "organization-secrets",
            element: <OrganizationSecretsPage />,
            handle: {
                label: "Secrets",
                menuItem: {
                    label: "Secrets",
                    section: "Organization",
                    to: "/settings/organization-secrets",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "agents",
            element: <AgentsPage />,
            handle: {
                label: "Agents",
                menuItem: {
                    label: "Agents",
                    section: "Organization",
                    to: "/settings/agents",
                    isAdminRole: true,
                    hidden: useHideAgentsPage,
                } satisfies MenuItem,
            },
        },
        {
            path: "org-integrations",
            element: <OrgIntegrationsPage />,
            handle: {
                label: "Integrations",
                menuItem: {
                    label: "Integrations",
                    section: "Organization",
                    to: "/settings/org-integrations",
                    isAdminRole: true,
                    hidden: useHideAgentsPage,
                } satisfies MenuItem,
            },
        },
        {
            path: "policies",
            element: <OrganizationPoliciesPage />,
            handle: {
                label: "Policies",
                menuItem: {
                    label: "Policies",
                    section: "Organization",
                    to: "/settings/policies",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "login",
            element: <LoginAndSecurityPage />,
            handle: {
                label: "Login and security",
                menuItem: {
                    label: "Login and security",
                    section: "Organization",
                    to: "/settings/login",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "billing",
            element: <BillingPage />,
            handle: {
                label: "Billing",
                menuItem: {
                    label: "Billing",
                    section: "Organization",
                    to: "/settings/billing",
                    isAdminRole: true,
                    hiddenOnTiers: [OrganizationTier.FREE, OrganizationTier.ENTERPRISE],
                } satisfies MenuItem,
            },
        },
        {
            path: "credit-usage",
            element: <CreditUsagePage />,
            handle: {
                label: "Usage",
                menuItem: {
                    label: "Usage",
                    section: "Organization",
                    to: "/settings/credit-usage",
                    isAdminRole: true,
                    hiddenOnTiers: [OrganizationTier.FREE, OrganizationTier.ENTERPRISE],
                    featureFlag: Flags.CreditUsageEnabled,
                } satisfies MenuItem,
            },
        },
    ],
};

// eslint-disable-next-line react-refresh/only-export-components
export const legacyOnboardingRoutes: RouteObject = {
    path: "/onboarding",
    children: [
        {
            path: "/onboarding/primary-interests",
            element: <Navigate to="/" replace />,
        },
        {
            path: "how-gitpod-works",
            element: <Navigate to="/" replace />,
        },
        {
            path: "set-up-runner/*",
            element: <Navigate to="/" replace />,
        },
        {
            path: "automate-your-dev-environment",
            element: <Navigate to="/" replace />,
        },
        {
            path: "share-with-your-team",
            element: <Navigate to="/" replace />,
        },
    ],
};

// eslint-disable-next-line react-refresh/only-export-components
export const onaOnboardingRoutes: RouteObject = {
    element: <AuthenticatedUserLayout />,
    children: [
        {
            path: "/onboarding/ona",
            element: <OnaOnboardingSetupPage />,
        },
        {
            path: "/onboarding/ona/connect-to-github",
            element: <OnaOnboardingPage step="connect-to-github" />,
        },
        {
            path: "/onboarding/ona/select-your-repository",
            element: <OnaOnboardingPage step="select-your-repository" />,
        },
        {
            path: "/onboarding/ona/get-started-with-tasks",
            element: <OnaOnboardingPage step="get-started-with-tasks" />,
        },
    ],
};

// eslint-disable-next-line react-refresh/only-export-components
export const appRoutes: RouteObject[] = [
    onaOnboardingRoutes,
    {
        // Pages using Sidebar
        element: <SidebarLayout />,
        children: [
            {
                element: <AuthenticatedUserLayout />,
                children: [
                    {
                        index: true,
                        element: <SlashRedirect />,
                    },
                    projectRoutes,
                    {
                        path: "ai",
                        children: [
                            {
                                index: true,
                                element: <OnaAIPage />,
                            },
                        ],
                    },
                    {
                        path: "insights",
                        element: <InsightsPage />,
                    },
                    {
                        path: "automations",
                        children: [
                            {
                                element: <AutomationsPage />,
                                handle: {
                                    layout: {
                                        noPadding: true,
                                    },
                                } satisfies RouteHandle,
                                children: [
                                    {
                                        index: true,
                                        element: null,
                                    },
                                    {
                                        path: "webhooks",
                                        element: (
                                            <RequireWebhooksCapability>
                                                <WebhooksSidePanel />
                                            </RequireWebhooksCapability>
                                        ),
                                        handle: {
                                            layout: {
                                                scrollable: false,
                                            },
                                        } satisfies RouteHandle,
                                        children: [
                                            {
                                                path: ":webhookId",
                                                element: <WebhookDetailsPage />,
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "new",
                                element: (
                                    <RequireFullAutomationAccess>
                                        <NewAutomationPage />
                                    </RequireFullAutomationAccess>
                                ),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                    },
                                } satisfies RouteHandle,
                            },
                            {
                                path: ":workflowId",
                                element: (
                                    <RequireFullAutomationAccess>
                                        <AutomationDetailsPage />
                                    </RequireFullAutomationAccess>
                                ),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                        scrollable: false,
                                    },
                                } satisfies RouteHandle,
                            },
                            {
                                path: ":workflowId/edit",
                                element: (
                                    <RequireFullAutomationAccess>
                                        <AutomationEditPage />
                                    </RequireFullAutomationAccess>
                                ),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                    },
                                } satisfies RouteHandle,
                            },
                            {
                                path: "executions/:workflowExecutionId",
                                element: (
                                    <RequireFullAutomationAccess>
                                        <AutomationExecutionLayout />
                                    </RequireFullAutomationAccess>
                                ),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                    },
                                } satisfies RouteHandle,
                                children: [
                                    {
                                        index: true,
                                        element: <AutomationExecutionSummaryPage />,
                                    },
                                    {
                                        path: "actions/:workflowExecutionActionId",
                                        element: <AutomationExecutionActionPage />,
                                    },
                                ],
                            },
                        ],
                    },
                    legacyOnboardingRoutes,
                    {
                        path: "create",
                        element: <Navigate to="/" replace state={{ showCreateModal: true }} />,
                    },
                ],
            },
        ],
    },
    // Environment details routes uses a custom content area
    {
        element: <SidebarLayout CustomContent={Outlet} />,
        children: [
            {
                element: (
                    <AuthenticatedUserLayout
                        // Wrapping the LoadingState in MainContent so that we render a full blank page
                        // while the page is loading. This is the default behavior of SidebarLayout if you aren't
                        // passing in CustomContent, so this just ensures that we have a consistent loading
                        // state.
                        LoadingComponent={() => (
                            <MainContent>
                                <LoadingState />
                            </MainContent>
                        )}
                    />
                ),
                children: [createEnvironmentDetailsRoute()],
            },
        ],
    },
    // Settings pages with custom sidebar
    {
        element: <SidebarLayout CustomSidebar={SettingsSidebar} disableCollapse />,
        children: [
            {
                element: <AuthenticatedUserLayout />,
                children: [settingsRoutes],
            },
        ],
    },

    {
        element: <AuthenticatedUserLayout />,
        children: [
            {
                path: "/create-organization",
                element: <CreateOrganizationPage />,
            },
            {
                path: "/integrations/connect/:integrationId",
                element: <ConnectIntegrationPage />,
            },
            {
                path: "/integrations/connected/:integrationId",
                element: <IntegrationConnectedPage />,
            },
        ],
    },

    // Experiments activation route - no sidebar, authenticated
    {
        element: <SidebarLayout CustomSidebar={NoSidebar} disableCollapse disableMobileMenu />,
        children: [
            {
                element: <AuthenticatedUserLayout />,
                children: [
                    {
                        path: "/experiments/:name",
                        element: <ExperimentsActivationPage />,
                    },
                ],
            },
        ],
    },

    // Pages without a Sidebar
    {
        element: <SidebarLayout CustomSidebar={NoSidebar} disableCollapse disableMobileMenu />,
        children: [
            {
                path: "/",
                element: <AuthenticatedUserLayout />,
                children: [
                    {
                        path: `${JOIN_ORGANIZATION_PATH}/:inviteId?`,
                        element: (
                            <PageSuspense>
                                <JoinOrganizationPage />
                            </PageSuspense>
                        ),
                    },
                ],
            },
        ],
    },
    // UNAUTHENTICATED ROUTES
    {
        path: "/login/ona",
        element: <Navigate to="/login" replace />,
    },
    {
        path: "/login/ona/new",
        element: <Navigate to="/login" replace />,
    },
    {
        children: [
            {
                path: "/login",
                element: (
                    <PageSuspense>
                        <LoginPage onaLayout />
                    </PageSuspense>
                ),
            },
        ],
    },
    // Pages without a Sidebar
    {
        element: <SidebarLayout CustomSidebar={NoSidebar} />,
        children: [
            {
                path: "/magiclink",
                element: (
                    <PageSuspense>
                        <MagiclinkPage />
                    </PageSuspense>
                ),
            },
            {
                path: "/confirm/signin",
                element: (
                    <PageSuspense>
                        <SigninDesktopPage />
                    </PageSuspense>
                ),
            },
            {
                path: "/local-runner",
                element: (
                    <PageSuspense>
                        <LocalRunner />
                    </PageSuspense>
                ),
            },
            {
                path: "/quit-app",
                element: (
                    <PageSuspense>
                        <QuitApp />
                    </PageSuspense>
                ),
            },
            {
                path: "/account-deleted",
                element: (
                    <PageSuspense>
                        <AccountDeletedPage />
                    </PageSuspense>
                ),
            },
            {
                path: "*",
                element: <NotFound />,
            },
        ],
    },

    // Standalone pages without any layout wrapper
    {
        path: "/runner/scm-success",
        element: <ScmSuccessPage />,
    },

    {
        children: [
            {
                path: "/details/:environmentId/code-changes",
                Component: EnvironmentCodeChanges,
            },
        ],
    },
];

/**
 * This component ensures that we watch for org-level events.
 *
 * This is a temporary solution until we can rework our route tree so that we only have one instantiation of AuthenticatedUserLayout
 * which would then be responsible for watching for org-level events.
 *
 * See AGENT-477
 */
const StreamOrgEvents = () => {
    // This aborts early if there there isn't a current user, so it's safe to call even on routes that render without an authenticated user
    const { unsubscribe } = useStreamEvents({ case: "organization", value: true });

    // This ensures that we unsubscribe/resubscribe to the event stream when the principal changes (the user changes org)
    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            unsubscribe(); // Unsubscribe from the previous event stream
            // This causes a re-render so we resubscribe to the event stream (with the updated principal)
            setPrincipal(getPrincipal());
        });
    }, [setPrincipal, unsubscribe]);

    return null;
};

/**
 * The primary purpose of the root route is to wrap the routes in an error boundary which
 * triggers instead of the default error boundary of the RouterProvider.
 */
const rootRoute = {
    element: (
        <ExceptionErrorBoundary>
            <PaymentFailureBanner />
            <TierViolationModal />
            <CreditsExhaustionModal />
            <VerifyCardModal />
            <StreamOrgEvents />
            <Outlet />
        </ExceptionErrorBoundary>
    ),
    children: appRoutes,
} satisfies RouteObject;

export const NewSidebarRouter = sentryCreateBrowserRouter([rootRoute]);
export const CreateTestRouter = (opts?: { initialEntries?: string[] }) => createMemoryRouter([rootRoute], opts);
